'use client'

import api, { setBackendAuthToken } from '@/lib/api'
import {
  OmiziAuthContext,
  OmiziAuthSession,
  OmiziPortalUser,
  clearStoredAuthSession,
  createEmptyAuthContext,
  createEmptyPortalUser,
  readStoredAuthSession,
  writeStoredAuthSession,
} from './session'

let bootstrapInFlight: Promise<OmiziAuthSession> | null = null
let bootstrapInFlightToken: string | null = null

function toStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string').map((entry) => entry.trim()).filter(Boolean)
  }

  if (typeof value === 'string' && value.trim()) {
    return [value.trim()]
  }

  return []
}

function toAudience(value: unknown): string | string[] {
  if (Array.isArray(value)) {
    const audience = toStringList(value)
    return audience.length <= 1 ? (audience[0] || '') : audience
  }

  if (typeof value === 'string') {
    return value.trim()
  }

  return ''
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.')
  if (parts.length < 2) {
    return {}
  }

  try {
    const payload = parts[1]
    const padding = payload.length % 4
    const padded = padding === 0 ? payload : `${payload}${'='.repeat(4 - padding)}`
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded) as Record<string, unknown>
  } catch {
    return {}
  }
}

function compactName(firstName?: string, lastName?: string, fallback?: string): string | undefined {
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()
  return fullName || fallback || undefined
}

export function normalizeAuthContextFromClaims(payload: Record<string, unknown>, tokenType: OmiziAuthContext['tokenType'] = 'user'): OmiziAuthContext {
  const permissions = toStringList(payload.permissions ?? payload.roles)
  const appRoles = toStringList(payload.roles)
  const groupIds = toStringList(payload.groups)
  const username = typeof payload.preferred_username === 'string'
    ? payload.preferred_username
    : typeof payload.username === 'string'
      ? payload.username
      : typeof payload.email === 'string'
        ? payload.email
        : typeof payload.mail === 'string'
          ? payload.mail
          : undefined

  return {
    subject: typeof payload.sub === 'string' ? payload.sub : typeof payload.user_id === 'string' ? payload.user_id : '',
    issuer: typeof payload.iss === 'string' ? payload.iss : '',
    audience: toAudience(payload.aud),
    azureTenantId: typeof payload.tid === 'string' ? payload.tid : typeof payload.azure_tenant_id === 'string' ? payload.azure_tenant_id : '',
    azureObjectId: typeof payload.oid === 'string' ? payload.oid : typeof payload.azure_object_id === 'string' ? payload.azure_object_id : undefined,
    clientApplicationId: typeof payload.azp === 'string'
      ? payload.azp
      : typeof payload.appid === 'string'
        ? payload.appid
        : undefined,
    userType: payload.omizi_user_type === 'internal' || payload.omizi_user_type === 'external'
      ? payload.omizi_user_type
      : undefined,
    omiziTenantId: typeof payload.omizi_tenant_id === 'string' ? payload.omizi_tenant_id : undefined,
    omiziBusinessId: typeof payload.omizi_business_id === 'string' ? payload.omizi_business_id : undefined,
    groupIds,
    appRoles,
    permissions,
    displayName: typeof payload.name === 'string' ? payload.name : typeof payload.display_name === 'string' ? payload.display_name : undefined,
    username,
    tokenType,
  }
}

export function normalizePortalUser(
  profile: Record<string, unknown>,
  authContext: OmiziAuthContext,
  permissions: string[]
): OmiziPortalUser {
  const firstName = typeof profile.first_name === 'string' ? profile.first_name : undefined
  const lastName = typeof profile.last_name === 'string' ? profile.last_name : undefined
  const email = typeof profile.email === 'string'
    ? profile.email
    : authContext.username || ''

  const displayName = compactName(firstName, lastName, authContext.displayName || email)

  return {
    id: authContext.subject || (typeof profile.id === 'string' ? profile.id : ''),
    uuid: typeof profile.uuid === 'string' ? profile.uuid : typeof profile.user_uuid === 'string' ? profile.user_uuid : undefined,
    email,
    userType: profile.user_type === 'internal' || profile.user_type === 'external'
      ? profile.user_type
      : authContext.userType,
    firstName,
    lastName,
    role: typeof profile.role === 'string' ? profile.role : undefined,
    displayName,
    tenantId: typeof profile.omizi_tenant_id === 'string' ? profile.omizi_tenant_id : authContext.omiziTenantId,
    businessId: typeof profile.omizi_business_id === 'string' ? profile.omizi_business_id : authContext.omiziBusinessId,
    azureTenantId: authContext.azureTenantId,
    azureObjectId: authContext.azureObjectId,
    clientApplicationId: authContext.clientApplicationId,
    groupIds: authContext.groupIds,
    appRoles: authContext.appRoles,
    permissions: permissions.length > 0 ? permissions : authContext.permissions,
    tokenType: authContext.tokenType,
    username: authContext.username,
    imageUrl: typeof profile.image_url === 'string' ? profile.image_url : undefined,
    publicMetadata: typeof profile.public_metadata === 'object' && profile.public_metadata !== null
      ? (profile.public_metadata as Record<string, unknown>)
      : undefined,
    emailAddresses: email ? [{ emailAddress: email }] : undefined,
  }
}

export function applyPortalAuthToken(accessToken: string | null): void {
  setBackendAuthToken(accessToken)
}

export async function bootstrapPortalAuthSession(accessToken: string): Promise<OmiziAuthSession> {
  if (bootstrapInFlight && bootstrapInFlightToken === accessToken) {
    return bootstrapInFlight
  }

  bootstrapInFlightToken = accessToken
  bootstrapInFlight = (async () => {
    applyPortalAuthToken(accessToken)

    const provisionalSession = readStoredAuthSession()
    if (provisionalSession?.accessToken !== accessToken) {
      writeStoredAuthSession({
        accessToken,
        authContext: provisionalSession?.authContext ?? createEmptyAuthContext(),
        user: provisionalSession?.user ?? createEmptyPortalUser(),
        permissions: provisionalSession?.permissions ?? [],
        fetchedAt: provisionalSession?.fetchedAt ?? new Date().toISOString(),
      })
    }

    const [profileResponse, permissionsResponse] = await Promise.all([
      api.get('/api/auth/me'),
      api.get<{ permissions: string[] }>('/api/auth/me/permissions'),
    ])

    const profile = (profileResponse.data ?? {}) as Record<string, unknown>
    const permissions = toStringList(permissionsResponse.data?.permissions)
    const tokenClaims = decodeJwtPayload(accessToken)
    const authContext = normalizeAuthContextFromClaims(
      {
        ...tokenClaims,
        ...profile,
        permissions: permissions.length > 0 ? permissions : tokenClaims.permissions,
        roles: tokenClaims.roles ?? permissions,
        groups: tokenClaims.groups ?? profile.groups,
        omizi_tenant_id: profile.omizi_tenant_id ?? tokenClaims.omizi_tenant_id,
        omizi_business_id: profile.omizi_business_id ?? tokenClaims.omizi_business_id,
        display_name: profile.display_name ?? tokenClaims.display_name,
        preferred_username: profile.email ?? tokenClaims.preferred_username,
      },
      'user'
    )
    const user = normalizePortalUser(profile, authContext, permissions.length > 0 ? permissions : authContext.permissions)

    const session: OmiziAuthSession = {
      accessToken,
      authContext,
      user,
      permissions: permissions.length > 0 ? permissions : authContext.permissions,
      fetchedAt: new Date().toISOString(),
    }

    writeStoredAuthSession(session)
    return session
  })()

  try {
    return await bootstrapInFlight
  } finally {
    if (bootstrapInFlightToken === accessToken) {
      bootstrapInFlight = null
      bootstrapInFlightToken = null
    }
  }
}

export async function hydratePortalAuthSessionFromStoredToken(): Promise<OmiziAuthSession | null> {
  const storedSession = readStoredAuthSession()
  if (!storedSession?.accessToken) {
    return null
  }

  try {
    return await bootstrapPortalAuthSession(storedSession.accessToken)
  } catch (error) {
    clearStoredAuthSession()
    applyPortalAuthToken(null)
    throw error
  }
}

export function createEmptyPortalAuthSession(): OmiziAuthSession {
  return {
    accessToken: '',
    authContext: createEmptyAuthContext(),
    user: createEmptyPortalUser(),
    permissions: [],
    fetchedAt: new Date(0).toISOString(),
  }
}
